import { Controller } from "@hotwired/stimulus"

/**
 * Turns an address into coordinates and coordinates into an address, one row at a time.
 *
 * Scoped to a single row of the online dealer form's Locations section: the operator
 * types an address, presses "Find coordinates" (or Enter in the field), and the server
 * renders its answer into the row's turbo frame. On a find, the coordinate fields and
 * the hidden region fields fill in and the address field takes Google's own spelling -
 * the button was the consent, Save is the commit, and nothing is written until then.
 *
 * It points a turbo frame at the check URL and lets the server render the answer. No
 * fetch, no JSON, and no user-facing copy in here - the frame's contents come from
 * ERB, which is why i18n-tasks is configured to ignore JS paths. The verdict and the
 * fill values arrive as data attributes on a wrapper inside the frame (Turbo swaps a
 * frame's children, so nothing rides on the tag itself), the same contract the
 * customer-number check uses.
 *
 * An explicit button rather than the number check's debounce-on-type: each question
 * here costs money, and nobody wants an answer about half an address.
 *
 * The other direction exists for the rows this feature inherited, which are a pin and
 * nothing else. #reverse asks what is AT the pin and fills the address half of the row
 * from the answer; it never moves the pin, because the pin is the question. The two
 * share one frame per row and the answer says which it is on data-geocode-direction.
 *
 * The name is the one field neither direction fills. Google can say the locality is
 * "Burbank"; it cannot know the branch is "Burbank DC", and that distinction is what the
 * worklist and the region reports are built on. The panel offers it and #useName is the
 * operator accepting it.
 *
 * The coordinates start hidden. They are derived from an address now, and a field
 * somebody can type into by accident is a field that gets typed into by accident - so
 * the row prints the pair instead, and where it came from. Hand-tuning a pin to a
 * warehouse entrance is a real thing operators do, and it is the only way in at all when
 * Google cannot resolve an address, so #unlockCoordinates is the deliberate way back:
 * it takes the printed pair down and puts the two inputs up in its place, which is a
 * change a reader can see happen. The inputs are hidden and never disabled or read-only:
 * hidden is enough, since a hidden input still submits, still takes a fill, and is still
 * what the ranking map reads.
 *
 * Expected markup (see _location_fields.html.erb):
 *   <div data-controller="location-lookup"
 *        data-location-lookup-url-value="/…/geocode-check"
 *        data-location-lookup-reverse-url-value="/…/reverse-geocode-check">
 *     <input data-location-lookup-target="label">
 *     <input data-location-lookup-target="address"
 *            data-action="input->location-lookup#invalidate
 *                         keydown.enter->location-lookup#check">
 *     <button type="button" data-action="location-lookup#check">
 *     <turbo-frame id="…_geocode" data-location-lookup-target="frame">
 *       …[data-geocode-state][data-geocode-direction][data-geocode-query]…, server-side
 *     <div data-location-lookup-target="pinSummary" [hidden]>
 *       <span data-location-lookup-target="coordinatesText">lat, lng</span>
 *       <span data-location-lookup-target="provenance" data-source="forward|reverse|hand" [hidden]>
 *       <button type="button" data-action="location-lookup#reverse">
 *       <button type="button" data-action="location-lookup#unlockCoordinates">
 *     <div data-location-lookup-target="pinEmpty" [hidden]>
 *       <button type="button" data-action="location-lookup#unlockCoordinates">
 *     <div data-location-lookup-target="coordinateFields" hidden>
 *       <input type="number" data-location-lookup-target="latitude|longitude"
 *              data-action="input->location-lookup#invalidatePin">
 *     <input type="hidden" data-location-lookup-target="countryCode|regionCode|regionName|source">
 *   </div>
 */
export default class extends Controller {
  static targets = ["address", "frame", "latitude", "longitude", "label",
                    "pinSummary", "pinEmpty", "coordinateFields", "coordinatesText", "provenance",
                    "countryCode", "regionCode", "regionName", "source"]
  static values = { url: String, reverseUrl: String }

  connect() {
    this.onFrameLoad = () => this.applyAnswer()
    if (this.hasFrameTarget) {
      this.frameTarget.addEventListener("turbo:frame-load", this.onFrameLoad)
    }
  }

  disconnect() {
    if (this.hasFrameTarget) {
      this.frameTarget.removeEventListener("turbo:frame-load", this.onFrameLoad)
    }
  }

  // Enter in the address field asks rather than submits - the form-level guard
  // already blocks the implicit submission; this makes the keystroke do what the
  // person pressing it meant.
  check(event) {
    if (event) event.preventDefault()
    if (!this.hasAddressTarget || !this.hasFrameTarget) return

    // Setting src to what it already is loads nothing, which would leave a retry
    // spinning forever. Asking the frame to reload is the same request, made again.
    const url = this.checkUrl(this.currentAddress())
    if (this.frameTarget.getAttribute("src") === url && typeof this.frameTarget.reload === "function") {
      this.frameTarget.reload()
    } else {
      this.frameTarget.src = url
    }
  }

  // Wired to the "did you mean" candidates the frame renders on an ambiguous answer:
  // re-asks with Google's own spelling, which is specific enough to come back as one.
  //
  // Through #fill rather than by assignment, because a click is not an input event and
  // the field is being edited all the same. The form watches for input to know it holds
  // unsaved work, so writing straight to .value put Google's spelling of the address in
  // the box with nothing marked: if the follow-up lookup then failed, the operator was
  // sitting on a changed address with no badge and no warning on the way out.
  //
  // #invalidate stays explicit even though the dispatched event also reaches it, so this
  // method still says what it does to the derived facts if that wiring ever moves. Both
  // paths clear the same fields, and #clear is a no-op the second time.
  pick(event) {
    if (!this.hasAddressTarget) return

    this.fill(this.addressTarget, event.params.address)
    this.invalidate()
    this.check()
  }

  // Wired to the retry the frame renders when Google did not answer. That is the one
  // state where asking the same question again can produce a different answer.
  retry() {
    this.check()
  }

  // The other direction: what is at this pin. For the rows that have only ever had
  // coordinates, and for any row whose typed address Google could not resolve.
  reverse(event) {
    if (event) event.preventDefault()
    if (!this.hasFrameTarget) return

    const url = this.reverseCheckUrl()
    if (this.frameTarget.getAttribute("src") === url && typeof this.frameTarget.reload === "function") {
      this.frameTarget.reload()
    } else {
      this.frameTarget.src = url
    }
  }

  retryReverse() {
    this.reverse()
  }

  // The operator accepting the suggested locality. A fill and not a lock: "Burbank" is
  // the start of a branch name far more often than it is the whole of one.
  useName(event) {
    if (!this.hasLabelTarget) return

    this.fill(this.labelTarget, event.params.name)
    this.labelTarget.focus()
  }

  // The deliberate way into a derived pair.
  //
  // The printed line goes and the two inputs come, in one move, because after this the
  // row is telling a different story: what is in these boxes is about to be hand-tuned
  // rather than derived from the address above, and a caption saying where a pair came
  // from would be describing numbers that are about to change. The cursor lands in the
  // first box, which is the other half of making the change visible - the reader asked
  // to type, and the place to type is where they are.
  //
  // One way, on purpose. There is no lock to put back: a pair somebody has typed into is
  // a hand-set pair until it is saved, and the padlock this replaced never re-locked
  // either.
  unlockCoordinates(event) {
    if (event) event.preventDefault()

    if (this.hasPinSummaryTarget) this.pinSummaryTarget.hidden = true
    if (this.hasPinEmptyTarget) this.pinEmptyTarget.hidden = true
    if (this.hasCoordinateFieldsTarget) this.coordinateFieldsTarget.hidden = false
    if (this.hasLatitudeTarget) this.latitudeTarget.focus()
  }

  // A hand-edited pin is no longer the pin a lookup produced, so the claim that a lookup
  // produced it goes: geocode_source is the provenance of the COORDINATES, and nil is
  // the model's word for a pair somebody typed. Left in place, a forward row whose pin
  // was nudged to the warehouse gate would come back from the save captioned "from the
  // address", and the caption would be describing numbers that are no longer Google's.
  //
  // What else goes depends on which way the lookup ran. A forward lookup's other facts -
  // the address, the country and region - describe the address the operator typed, and
  // moving the pin under them is exactly the hand-tune the unlock exists for, so they
  // stay; #invalidate is what clears those, when the address itself changes. A reverse
  // answer IS the address - it was read off the pin and nobody ever typed or confirmed
  // it - so leaving it behind would save an address sourced from coordinates it no
  // longer sits on, with the most legible fact on the row still claiming to describe
  // them. It goes, and the region facts derived with it go too.
  //
  // Guarded against its own side effects: #fill dispatches input so anything listening
  // sees a programmatic write like a typed one, and without the guard the coordinates
  // that a forward answer had just filled would clear the source it was about to fill in
  // the next line.
  invalidatePin() {
    if (this.applying) return
    if (!this.source || this.source.value === "") return

    if (this.source.value === "reverse") {
      this.clear(this.address)
      this.clear(this.countryCode)
      this.clear(this.regionCode)
      this.clear(this.regionName)
      this.clearAnswer()
    }
    this.clear(this.source)
  }

  // Typing in the address field after a lookup: the derived region facts described
  // the address they arrived with and must not outlive it, so they clear. The
  // coordinates are deliberately left alone - they are the operational fields, they
  // may have been hand-tuned, and a half-edited address is not a reason to blank a
  // valid pin.
  invalidate() {
    this.clear(this.countryCode)
    this.clear(this.regionCode)
    this.clear(this.regionName)
    this.clear(this.source)
    this.clearAnswer()
  }

  // The panel describes the address it arrived with, so it must not outlive that
  // address any more than the hidden facts do. Left on screen under something freshly
  // typed, a "Matched" line reads as an answer about what the operator is looking at
  // now - the same misreading applyAnswer already refuses to make with the fields.
  //
  // Empties the frame rather than removing it: Turbo swaps by id, so the element has
  // to still be there for the next lookup to land in.
  clearAnswer() {
    if (this.hasFrameTarget) this.frameTarget.innerHTML = ""
  }

  applyAnswer() {
    const answer = this.hasFrameTarget ? this.frameTarget.querySelector("[data-geocode-state]") : null
    if (!answer) return

    const reverse = answer.dataset.geocodeDirection === "reverse"

    // An answer about a question that has since been edited fills nothing, and is not
    // left on screen either. Without this, a slow response could overwrite the newer
    // value - and worse, claim region facts about it that describe the older one -
    // while the panel went on presenting it as the answer to what is in the field now.
    // Each direction is stale against its own input: forward against the address it was
    // asked about, reverse against the pin.
    //
    // Asked of EVERY state, before the match is separated out. A "no address here" or a
    // "Google did not answer" is just as much an answer about the pin that was sent, and
    // returning on the state first left those sitting under a pin somebody had since
    // moved - the panel patiently reporting a failure about coordinates no longer on the
    // row. The frame carries the query whatever the verdict, precisely so this can ask.
    const asked = reverse ? this.currentCoordinates() : this.currentAddress()
    if (answer.dataset.geocodeQuery !== asked) {
      this.clearAnswer()
      return
    }

    // Only a match fills anything in. The states above are shown, not applied.
    if (answer.dataset.geocodeState !== "found") return

    // See #invalidatePin: the fills below dispatch input, and two of them are fields that
    // listen for it.
    this.applying = true
    try {
      if (this.hasAddressTarget) this.addressTarget.value = answer.dataset.geocodeAddress

      // The pin is what reverse was ASKED about, so it is the one thing an answer to it
      // may not touch. Forward derived it and fills it.
      if (!reverse) {
        this.fill(this.hasLatitudeTarget ? this.latitudeTarget : null, answer.dataset.geocodeLatitude)
        this.fill(this.hasLongitudeTarget ? this.longitudeTarget : null, answer.dataset.geocodeLongitude)
      }

      this.fill(this.countryCode, answer.dataset.geocodeCountryCode)
      this.fill(this.regionCode, answer.dataset.geocodeRegionCode)
      this.fill(this.regionName, answer.dataset.geocodeRegionName)
      this.fill(this.source, reverse ? "reverse" : "forward")
    } finally {
      this.applying = false
    }

    // What the row prints about the pin has to follow what the fill just did to it. A
    // forward answer put a pair in the boxes, so the printed line shows that pair and
    // says it came from the address; a reverse answer left the pin alone and read the
    // address off it, which is the other caption.
    if (reverse) {
      this.showProvenance("reverse")
    } else {
      this.showPin("forward")
    }

    // The eye lands where the review starts - but only while the operator is still
    // in this row. A slow answer landing after they have moved on to another field
    // must not yank the caret out from under them. Reverse is reviewed at the address.
    // Forward is reviewed at the pin it derived, which is the printed pair unless the
    // boxes have been opened - a hidden input cannot take focus, and asking it to would
    // quietly leave the caret where it was.
    const landing = reverse
      ? (this.hasAddressTarget ? this.addressTarget : null)
      : (this.unlocked && this.hasLatitudeTarget ? this.latitudeTarget : null)
    if (landing && this.element.contains(document.activeElement)) {
      landing.focus()
    }
  }

  // Prints the pair the boxes now hold and says where it came from, unless the boxes
  // themselves are on screen - then they are the display, and there is nothing to
  // summarise beside them.
  //
  // The pair is data, not copy: two numbers off the fields, joined the way a coordinate
  // is written. Every sentence around it is ERB.
  showPin(source) {
    if (this.unlocked) return

    if (this.hasCoordinatesTextTarget) {
      this.coordinatesTextTarget.textContent =
        `${this.fieldValue(this.hasLatitudeTarget ? this.latitudeTarget : null)}, ` +
        `${this.fieldValue(this.hasLongitudeTarget ? this.longitudeTarget : null)}`
    }
    this.showProvenance(source)
    if (this.hasPinSummaryTarget) this.pinSummaryTarget.hidden = false
    if (this.hasPinEmptyTarget) this.pinEmptyTarget.hidden = true
  }

  // One caption showing of the three the row carries. They are all in the markup so the
  // controller never has to say anything; it only picks.
  showProvenance(source) {
    this.provenanceTargets.forEach((caption) => {
      caption.hidden = caption.dataset.source !== source
    })
  }

  // Whether Edit coordinates has been pressed on this row: the inputs are showing and
  // the printed pair is not.
  get unlocked() {
    return this.hasCoordinateFieldsTarget && !this.coordinateFieldsTarget.hidden
  }

  checkUrl(address) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("address", address)
    url.searchParams.set("frame_id", this.frameTarget.id)
    return url.toString()
  }

  reverseCheckUrl() {
    const url = new URL(this.reverseUrlValue, window.location.origin)
    url.searchParams.set("latitude", this.fieldValue(this.hasLatitudeTarget ? this.latitudeTarget : null))
    url.searchParams.set("longitude", this.fieldValue(this.hasLongitudeTarget ? this.longitudeTarget : null))
    url.searchParams.set("frame_id", this.frameTarget.id)
    return url.toString()
  }

  currentAddress() {
    return this.hasAddressTarget ? this.addressTarget.value.trim() : ""
  }

  // The shape the server echoes back as the query, so the two can be compared as
  // strings. Never re-formatted: "34.0928" and "34.09280" are the same coordinate and
  // different strings, and this is only ever an equality check against the input that
  // produced the answer.
  currentCoordinates() {
    return `${this.fieldValue(this.hasLatitudeTarget ? this.latitudeTarget : null)},` +
           `${this.fieldValue(this.hasLongitudeTarget ? this.longitudeTarget : null)}`
  }

  fieldValue(field) {
    return field ? field.value.trim() : ""
  }

  // The input event is dispatched so anything listening to these fields - now or
  // later - sees a programmatic fill the same way it sees typing.
  fill(field, value) {
    if (!field) return

    field.value = value == null ? "" : value
    field.dispatchEvent(new Event("input", { bubbles: true }))
  }

  clear(field) {
    if (field && field.value !== "") {
      field.value = ""
    }
  }

  get address() {
    return this.hasAddressTarget ? this.addressTarget : null
  }

  get countryCode() {
    return this.hasCountryCodeTarget ? this.countryCodeTarget : null
  }

  get regionCode() {
    return this.hasRegionCodeTarget ? this.regionCodeTarget : null
  }

  get regionName() {
    return this.hasRegionNameTarget ? this.regionNameTarget : null
  }

  get source() {
    return this.hasSourceTarget ? this.sourceTarget : null
  }
}
